import React, { useState, useEffect, useCallback} from 'react';
import { Loader2, FileText, Clock } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { cn } from '../../lib/utils';
import { API, AUDIT_ACTION_CONFIG, formatAuditTimestamp } from './docTypesShared';

export const AuditLogPanel = ({ membreId, token }) => {
  const [open, setOpen] = useState(false);
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [actionFilter, setActionFilter] = useState('all');

  const fetchAuditLog = useCallback(async () => {
    if (!token || !membreId) return;
    setLoading(true);
    try {
      const params = actionFilter !== 'all' ? { action: actionFilter } : {};
      const res = await axios.get(
        `${API}/api/admin/membres/${membreId}/audit-log`,
        { headers: { Authorization: `Bearer ${token}` }, params },
      );
      setEntries(res.data?.entries || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Impossible de charger le journal d\'audit');
      setEntries([]);
    } finally {
      setLoading(false);
    }
  }, [token, membreId, actionFilter]);

  useEffect(() => {
    if (open) fetchAuditLog();
  }, [open, fetchAuditLog]);

  return (
    <div className="mt-4 rounded-lg border border-gray-200 bg-gray-50/50" data-testid="audit-log-panel">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-3 py-2 text-xs font-semibold text-gray-700 hover:bg-gray-100/50 transition rounded-lg"
        data-testid="toggle-audit-log-panel"
      >
        <span className="flex items-center gap-2">
          <Clock className="h-3.5 w-3.5" />
          Journal d'activité (audit RGPD)
          {entries.length > 0 && open && <span className="text-[10px] text-gray-500">— {entries.length} action(s)</span>}
        </span>
        <span className="text-gray-500">{open ? '−' : '+'}</span>
      </button>
      {open && (
        <div className="p-3 border-t border-gray-200 space-y-2">
          {/* Filtre par action */}
          <div className="flex flex-wrap gap-1">
            {[
              { v: 'all', l: 'Toutes' },
              { v: 'view', l: 'Consultations' },
              { v: 'download', l: 'Téléchargements' },
              { v: 'zip_export', l: 'ZIP' },
              { v: 'delete', l: 'Suppressions' },
            ].map(opt => (
              <button
                key={opt.v}
                type="button"
                onClick={() => setActionFilter(opt.v)}
                className={cn(
                  "text-[10px] px-2 py-0.5 rounded-full border transition-colors",
                  actionFilter === opt.v
                    ? "bg-gray-700 text-white border-gray-800"
                    : "bg-white text-gray-700 border-gray-300 hover:bg-gray-100",
                )}
                data-testid={`audit-filter-${opt.v}`}
              >
                {opt.l}
              </button>
            ))}
          </div>
          {/* Liste des entrées */}
          {loading ? (
            <div className="flex items-center justify-center py-3"><Loader2 className="h-4 w-4 animate-spin text-gray-500" /></div>
          ) : entries.length === 0 ? (
            <div className="text-xs text-gray-500 italic py-2 text-center" data-testid="audit-log-empty">
              Aucune action enregistrée pour ce membre
            </div>
          ) : (
            <div className="space-y-1 max-h-[200px] overflow-y-auto">
              {entries.map(e => {
                const conf = AUDIT_ACTION_CONFIG[e.action] || {
                  label: e.action,
                  color: 'text-gray-700 bg-gray-50 border-gray-200',
                  icon: FileText};
                const Icon = conf.icon;
                const details = e.details || {};
                const detailLine = details.original_filename
                  || details.zip_filename
                  || (details.total_included !== undefined ? `${details.total_included} document(s)` : '');
                return (
                  <div key={e.id} className="flex items-start gap-2 bg-white px-2 py-1.5 rounded border border-gray-100 text-[11px]" data-testid={`audit-entry-${e.id}`}>
                    <span className={cn("inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full border text-[10px] flex-shrink-0", conf.color)}>
                      <Icon className="h-2.5 w-2.5" />
                      {conf.label}
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="text-gray-800 truncate">
                        <span className="font-medium">{e.actor_email}</span>
                        {e.actor_role && <span className="text-gray-500"> ({e.actor_role})</span>}
                        {detailLine && <> — <span className="text-gray-700">{detailLine}</span></>}
                      </div>
                      <div className="text-gray-500 text-[10px] mt-0.5 flex items-center gap-2 flex-wrap">
                        <span>{formatAuditTimestamp(e.timestamp)}</span>
                        {e.ip_address && <span>· IP {e.ip_address}</span>}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
